// main tools
import { signIn } from 'next-auth/react'
import { useTranslation } from 'react-i18next'
import { useRouter } from 'next/router'
import { useState } from 'react'
import Link from 'next/link'

// components
import { ChangeLanguage } from '@dr-keyword-molecules/change-language'
import { IconComponent } from '@centria-ui-atoms/icon-component'
import { Input, InputPassword } from '@centria-ui-atoms/input'
import { Typography } from '@centria-ui-atoms/typography'
import { Section } from '@centria-ui-atoms/section'
import { Button } from '@centria-ui-atoms/button'

// utils
import { INITIAL_STATE } from './utils'

// services
import { LoginPageService } from '@dr-keyword-services/login'

// styles
import classes from './styles.module.scss'

// types
import type { ChangeType, SubmitType } from '@centria-ui/types'
import type { FC } from 'react'

type LoginFormProps = {
  handleShowForgottenPassword: () => void
}

// The login provider signals an MFA account by throwing this prefix with the
// challenge token appended, since a failed authorize can only return a string.
const MFA_PREFIX = 'MFA_REQUIRED:'

export const LoginForm: FC<LoginFormProps> = ({
  handleShowForgottenPassword
}) => {
  const { t } = useTranslation()
  const { replace, query } = useRouter()
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState(INITIAL_STATE)
  // Set once the password step passes on an MFA account; its presence flips
  // the form to the code step.
  const [challengeToken, setChallengeToken] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const { showFormattedError, showError } = LoginPageService.getBaseService()

  /**
   * @description handle change input
   */
  const handleChange = (ev: ChangeType) =>
    setData({ ...data, [ev.target.name]: ev.target.value })

  /**
   * @description Where to land after signing in. Normally the dashboard, but a
   * page that sent the user here to log in can ask to get them back — the MCP
   * consent screen does, and without it the authorization it was in the middle
   * of is abandoned.
   *
   * Only a path within this site is accepted: an absolute URL, or one starting
   * with `//`, would turn the login into an open redirect.
   */
  const callbackUrl =
    typeof query.callbackUrl === 'string' &&
    query.callbackUrl.startsWith('/') &&
    !query.callbackUrl.startsWith('//')
      ? query.callbackUrl
      : '/'

  /**
   * @description first step: email + password. On an MFA account the provider
   * answers with the challenge instead of a session, and the form moves to the
   * code step rather than reporting an error.
   */
  const handleSubmit = async (ev: SubmitType) => {
    ev.preventDefault()

    setLoading(true)

    const res = await signIn('login', {
      ...data,
      redirect: false,
      callbackUrl
    })

    if (res?.error?.startsWith(MFA_PREFIX)) {
      setChallengeToken(res.error.slice(MFA_PREFIX.length))
    } else if (res?.error) showFormattedError({ enum: res.error })
    else if (res?.ok) replace(callbackUrl)
    else showError('Ha ocurrido un error inesperado.')

    setLoading(false)
  }

  /**
   * @description second step: the six-digit code from the authenticator, or a
   * recovery code. Exchanges the challenge for the real session.
   */
  const handleVerify = async (ev: SubmitType) => {
    ev.preventDefault()

    setLoading(true)

    const res = await signIn('verifyMfa', {
      challengeToken,
      code: code.trim(),
      redirect: false,
      callbackUrl
    })

    if (res?.error) {
      // An expired challenge means the five-minute window elapsed; send the
      // user back to the password step rather than leaving them stuck.
      if (res.error === 'MFA_CHALLENGE_EXPIRED') {
        setChallengeToken(null)
        setCode('')
      }
      showFormattedError({ enum: res.error })
    } else if (res?.ok) replace(callbackUrl)
    else showError('Ha ocurrido un error inesperado.')

    setLoading(false)
  }

  if (challengeToken) {
    return (
      <Section size='lg' onSubmit={handleVerify} as='form'>
        <Typography
          Tag='h2'
          fontSize='subtitle'
          fontcolor='primary'
          fontWeight='semiBold'
          className={classes.subtitle}>
          {t('login-page.mfa.title')}
        </Typography>
        <Typography Tag='p' fontSize='small' className='mb-4'>
          {t('login-page.mfa.description')}
        </Typography>
        <Input
          id='mfa-code'
          type='text'
          name='code'
          autoFocus
          className='mb-4'
          inputColor='light'
          inputVariant='block'
          value={code}
          onChange={(ev: ChangeType) => setCode(ev.target.value)}
          label={t('login-page.mfa.code')}
        />
        <Button
          type='submit'
          loading={loading}
          variant='secondary'
          className={classes.button}>
          {t('login-page.mfa.action-button')}
        </Button>
        <Typography
          fontSize='small'
          className={classes.actionLabel}
          onClick={() => {
            setChallengeToken(null)
            setCode('')
          }}>
          <IconComponent className='me-2' icon='LockIcon' color='inherit' />
          {t('login-page.mfa.back')}
        </Typography>
      </Section>
    )
  }

  return (
    <Section size='lg' onSubmit={handleSubmit} as='form'>
      <Typography
        Tag='h2'
        fontSize='subtitle'
        fontcolor='primary'
        fontWeight='semiBold'
        className={classes.subtitle}>
        {t('login-page.title')}
      </Typography>
      <Input
        id='email'
        type='email'
        name='email'
        className='mb-4'
        inputColor='light'
        value={data.email}
        inputVariant='block'
        onChange={handleChange}
        label={t('login-page.email')}
      />
      <InputPassword
        name='password'
        className='mb-4'
        feedback={false}
        inputId='password'
        inputColor='light'
        inputVariant='block'
        value={data.password}
        onChange={handleChange}
        label={t('login-page.password')}
      />

      <Button
        type='submit'
        loading={loading}
        variant='secondary'
        className={classes.button}>
        {t('login-page.action-button')}
      </Button>
      <Typography Tag='label' fontSize='small' className={classes.actionLabel}>
        {t('login-page.question')} -{' '}
        <Link role='button' href='/registro'>
          <Typography Tag='span' fontSize='button' fontcolor='primary'>
            {t('login-page.register')}
          </Typography>
        </Link>
      </Typography>
      <Typography
        fontSize='small'
        className={classes.actionLabel}
        onClick={handleShowForgottenPassword}>
        <IconComponent className='me-2' icon='LockIcon' color='inherit' />
        {t('login-page.forgot-password')}
      </Typography>
      <div className='d-flex justify-content-center mt-2'>
        <ChangeLanguage />
      </div>
    </Section>
  )
}
